import { trDisplay } from './i18n';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './i18n/LanguageSelector';
import FeedbackButton from './components/FeedbackButton';
import React, { lazy, Suspense, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import './App.css';
import SafeWithdrawalPage from './components/swr/SafeWithdrawalPage';
import { runSafeWithdrawalSimulation, runSWRSafeWithdrawalAnalysis, runSWRStressTests } from './lib/swrWorkerClient';
import { isSWRAbortError } from './lib/swrJobManager';
import { beginSWRActivityPlan, cancelSWRActivityPlan, swrActivityMonitor } from './lib/swrActivityMonitor';
import SWRCalculationStatus from './components/swr/SWRCalculationStatus';
import { patchSWRScenarioResults } from './lib/swrScenarioStore';
import { SWR_SCENARIOS_CHANGED_EVENT } from './components/swr/SWRScenarioSavePanel';
import {
  SWR_SAFE_WITHDRAWAL_ANALYSIS_MODES,
  getSWRSafeWithdrawalDetailedSimulationCount,
} from './lib/swrSimulationModes';
import { Calculator, LineChart, CreditCard, Wallet, GitCompare, MoreHorizontal, ChevronDown } from 'lucide-react';

const ComparisonTab = lazy(() => import('./components/ComparisonTab'));
const SWRScenarioComparison = lazy(() => import('./components/swr/SWRScenarioComparison'));
const LegacyLombardPage = lazy(() => import('./components/LegacyLombardPage'));

const ViewLoadingFallback = () => (
  <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">{trDisplay("Caricamento della sezione...")}</div>
);

const paramsEqual = (a, b) => {
  if (!a || !b) return false;
  const keysToCompare = [
    'initialCapital', 'expectedReturn', 'annualVolatility', 'maxSingleYearDrawdown',
    'lombardInterestRate', 'netWithdrawalPerYear', 'inflationRate', 'maxLTV',
    'yearsToSimulate', 'numberOfSimulations', 'randomSeed'
  ];
  return keysToCompare.every(key => a[key] === b[key]);
};

function App() {
  useTranslation();
  const [activeTab, setActiveTab] = useState('swr');
  const [visitedTabs, setVisitedTabs] = useState(() => new Set(['swr']));
  const [secondaryViewsOpen, setSecondaryViewsOpen] = useState(false);

  const activateTab = useCallback((tab) => {
    setVisitedTabs(previous => {
      if (previous.has(tab)) return previous;
      const next = new Set(previous);
      next.add(tab);
      return next;
    });
    setActiveTab(tab);
  }, []);

  const [lombardResults, setLombardResults] = useState(null);
  const [lombardIsLoading, setLombardIsLoading] = useState(false);
  const [lombardProgress, setLombardProgress] = useState(0);
  const [lombardHasRun, setLombardHasRun] = useState(false);
  const [lombardScenarioParams, setLombardScenarioParams] = useState(null);
  const [lombardLastRunParams, setLombardLastRunParams] = useState(null);
  const [lombardCurrentFormParams, setLombardCurrentFormParams] = useState(null);
  const [currentMaxLTV, setCurrentMaxLTV] = useState(0.50);

  const lombardHasPendingChanges = useMemo(() => {
    if (!lombardHasRun || !lombardLastRunParams || !lombardCurrentFormParams) return false;
    return !paramsEqual(lombardLastRunParams, lombardCurrentFormParams);
  }, [lombardHasRun, lombardLastRunParams, lombardCurrentFormParams]);

  const handleLombardFormChange = useCallback((params) => {
    setLombardCurrentFormParams(params);
  }, []);

  const handleRunLombardSimulation = useCallback(async (params) => {
    setLombardIsLoading(true);
    setLombardProgress(0);
    setCurrentMaxLTV(params.maxLTV);
    setLombardScenarioParams(params);

    try {
      const { runMonteCarloSimulation } = await import('./lib/simulation/lombardEngine');
      const simulationResults = await runMonteCarloSimulation(params, (p) => setLombardProgress(p));
      setLombardResults(simulationResults);
      setLombardHasRun(true);
      setLombardLastRunParams(params);
      setLombardCurrentFormParams(params);
    } catch (error) {
      console.error('Errore durante la simulazione:', error);
    } finally {
      setLombardIsLoading(false);
      setLombardProgress(100);
    }
  }, []);

  const [swrResults, setSwrResults] = useState(null);
  const [swrIsLoading, setSwrIsLoading] = useState(false);
  const [swrProgress, setSwrProgress] = useState(0);
  const [swrHasRun, setSwrHasRun] = useState(false);
  const [swrScenarioParams, setSwrScenarioParams] = useState(null);
  const [swrLastRunParams, setSwrLastRunParams] = useState(null);
  const [swrCurrentFormParams, setSwrCurrentFormParams] = useState(null);
  const [swrSafeWithdrawalIsLoading, setSwrSafeWithdrawalIsLoading] = useState(false);
  const [swrSafeWithdrawalProgress, setSwrSafeWithdrawalProgress] = useState(null);
  const [swrSafeWithdrawalMode, setSwrSafeWithdrawalMode] = useState(SWR_SAFE_WITHDRAWAL_ANALYSIS_MODES.quick.id);
  const [swrRestoredScenarioParams, setSwrRestoredScenarioParams] = useState(null);
  const [swrRestoreKey, setSwrRestoreKey] = useState(0);
  const [swrStressTestsIsLoading, setSwrStressTestsIsLoading] = useState(false);
  const [swrStressTestsProgress, setSwrStressTestsProgress] = useState(null);
  const swrSimulationJobRef = useRef(0);
  const swrSafeWithdrawalJobRef = useRef(0);
  const swrStressTestsJobRef = useRef(0);
  const swrSafeWithdrawalProgressFrameRef = useRef(null);
  const swrSafeWithdrawalPendingProgressRef = useRef(null);
  const swrSafeWithdrawalLastProgressRef = useRef(null);
  const swrStressTestsProgressFrameRef = useRef(null);
  const swrStressTestsPendingProgressRef = useRef(null);
  const swrStressTestsLastProgressRef = useRef(null);
  const swrCompletedRunRef = useRef(null);
  const [swrCompletedRun, setSwrCompletedRun] = useState(null);
  const swrSimulationPendingRef = useRef(false);
  const swrAbortRef = useRef({ main: null, cards: null, stress: null });
  const swrActivityPlanRef = useRef(null);
  const beginSWRWork = useCallback(kind => {
    swrAbortRef.current[kind]?.abort();
    const controller = new AbortController();
    swrAbortRef.current[kind] = controller;
    return controller;
  }, []);
  useEffect(() => () => {
    Object.values(swrAbortRef.current).forEach(c => c?.abort());
    cancelSWRActivityPlan(swrActivityPlanRef.current);
  }, []);

  const handleSwrFormChange = useCallback((params) => {
    setSwrCurrentFormParams(params);
  }, []);

  const normalizeSafeWithdrawalProgress = useCallback((progressUpdate, fallbackPhase) => {
    if (typeof progressUpdate === 'object' && progressUpdate !== null) {
      return {
        percent: Math.max(0, Math.min(100, Math.round(progressUpdate.percent || 0))),
        phase: progressUpdate.phase || fallbackPhase,
      };
    }
    return {
      percent: Math.max(0, Math.min(100, Math.round(progressUpdate || 0))),
      phase: fallbackPhase,
    };
  }, []);

  const setSafeWithdrawalProgressIfChanged = useCallback((nextProgress) => {
    const previous = swrSafeWithdrawalLastProgressRef.current;
    if (
      previous &&
      previous.percent === nextProgress.percent &&
      previous.phase === nextProgress.phase
    ) {
      return;
    }
    swrSafeWithdrawalLastProgressRef.current = nextProgress;
    setSwrSafeWithdrawalProgress(nextProgress);
  }, []);

  const setStressTestsProgressIfChanged = useCallback((nextProgress) => {
    const previous = swrStressTestsLastProgressRef.current;
    if (
      previous &&
      previous.percent === nextProgress.percent &&
      previous.phase === nextProgress.phase
    ) {
      return;
    }
    swrStressTestsLastProgressRef.current = nextProgress;
    setSwrStressTestsProgress(nextProgress);
  }, []);

  const notifySavedScenarioUpdated = useCallback((scenarioId, updatedFields = {}) => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(SWR_SCENARIOS_CHANGED_EVENT, {
      detail: { scenarioId, autoUpdated: true, ...updatedFields },
    }));
  }, []);

  const updateSavedSwrScenariosForRun = useCallback((run, resultPatch, scenarioIds) => {
    const savedIds = scenarioIds || Array.from(run?.savedScenarioIds || []);
    if (!savedIds.length || !resultPatch) return;
    // Preserve completion order, including reconciliation after a slow initial save.
    run.archiveUpdates = run.archiveUpdates.then(async () => {
      await Promise.all(savedIds.map(async (scenarioId) => {
        const updated = await patchSWRScenarioResults(scenarioId, resultPatch);
        if (updated) notifySavedScenarioUpdated(scenarioId, { patchedResults: Object.keys(resultPatch) });
      }));
    }).catch(error => {
      console.error('Errore aggiornamento automatico scenario SWR salvato:', error);
    });
    return run.archiveUpdates;
  }, [notifySavedScenarioUpdated]);

  const handleSwrScenarioSaved = useCallback((snapshot) => {
    if (!snapshot?.id || !swrCompletedRun) return;
    // This callback belongs to the rendered run, even if a later run has completed.
    swrCompletedRun.savedScenarioIds.add(snapshot.id);
    if (Object.keys(swrCompletedRun.resultPatch).length) {
      updateSavedSwrScenariosForRun(swrCompletedRun, { ...swrCompletedRun.resultPatch }, [snapshot.id]);
    }
  }, [swrCompletedRun, updateSavedSwrScenariosForRun]);

  const scheduleSafeWithdrawalProgress = useCallback((progressUpdate, fallbackPhase = 'Calcolo prelievo sostenibile') => {
    swrSafeWithdrawalPendingProgressRef.current = normalizeSafeWithdrawalProgress(progressUpdate, fallbackPhase);
    if (swrSafeWithdrawalProgressFrameRef.current !== null) return;

    const flushProgress = () => {
      swrSafeWithdrawalProgressFrameRef.current = null;
      const pendingProgress = swrSafeWithdrawalPendingProgressRef.current;
      swrSafeWithdrawalPendingProgressRef.current = null;
      if (pendingProgress) {
        setSafeWithdrawalProgressIfChanged(pendingProgress);
      }
    };

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      swrSafeWithdrawalProgressFrameRef.current = window.requestAnimationFrame(flushProgress);
    } else {
      swrSafeWithdrawalProgressFrameRef.current = setTimeout(flushProgress, 16);
    }
  }, [normalizeSafeWithdrawalProgress, setSafeWithdrawalProgressIfChanged]);

  const cancelScheduledSafeWithdrawalProgress = useCallback(() => {
    const frameId = swrSafeWithdrawalProgressFrameRef.current;
    if (frameId === null) return;
    if (typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(frameId);
    } else {
      clearTimeout(frameId);
    }
    swrSafeWithdrawalProgressFrameRef.current = null;
    swrSafeWithdrawalPendingProgressRef.current = null;
  }, []);

  useEffect(() => () => {
    cancelScheduledSafeWithdrawalProgress();
  }, [cancelScheduledSafeWithdrawalProgress]);

  const scheduleStressTestsProgress = useCallback((progressUpdate, fallbackPhase = 'Calcolo stress test') => {
    swrStressTestsPendingProgressRef.current = normalizeSafeWithdrawalProgress(progressUpdate, fallbackPhase);
    if (swrStressTestsProgressFrameRef.current !== null) return;

    const flushProgress = () => {
      swrStressTestsProgressFrameRef.current = null;
      const pendingProgress = swrStressTestsPendingProgressRef.current;
      swrStressTestsPendingProgressRef.current = null;
      if (pendingProgress) {
        setStressTestsProgressIfChanged(pendingProgress);
      }
    };

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      swrStressTestsProgressFrameRef.current = window.requestAnimationFrame(flushProgress);
    } else {
      swrStressTestsProgressFrameRef.current = setTimeout(flushProgress, 16);
    }
  }, [normalizeSafeWithdrawalProgress, setStressTestsProgressIfChanged]);

  const cancelScheduledStressTestsProgress = useCallback(() => {
    const frameId = swrStressTestsProgressFrameRef.current;
    if (frameId === null) return;
    if (typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(frameId);
    } else {
      clearTimeout(frameId);
    }
    swrStressTestsProgressFrameRef.current = null;
    swrStressTestsPendingProgressRef.current = null;
  }, []);

  useEffect(() => () => {
    cancelScheduledStressTestsProgress();
  }, [cancelScheduledStressTestsProgress]);

  const runSafeWithdrawalCardsAnalysis = useCallback(async (params, mode = SWR_SAFE_WITHDRAWAL_ANALYSIS_MODES.quick.id, activityId) => {
    if (!params) return null;
    const run = swrCompletedRunRef.current;
    if (!run) return null;

    cancelScheduledSafeWithdrawalProgress();
    const jobId = ++swrSafeWithdrawalJobRef.current;
    const controller = beginSWRWork('cards');
    const isDetailed = mode === SWR_SAFE_WITHDRAWAL_ANALYSIS_MODES.detailed.id;
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const numberOfSimulations = isDetailed
      ? getSWRSafeWithdrawalDetailedSimulationCount(isMobile)
      : SWR_SAFE_WITHDRAWAL_ANALYSIS_MODES.quick.simulations;

    setSwrSafeWithdrawalMode(mode);
    setSwrSafeWithdrawalIsLoading(true);
    setSafeWithdrawalProgressIfChanged({
      percent: 0,
      phase: isDetailed ? 'Analisi dettagliata delle card' : 'Stima rapida delle card',
    });

    try {
      const analysis = await runSWRSafeWithdrawalAnalysis(
        {
          ...params,
          randomSeed: params.effectiveRandomSeed ?? params.randomSeed,
        },
        { mode, numberOfSimulations, isMobile },
        (progressUpdate) => {
          if (jobId !== swrSafeWithdrawalJobRef.current) return;
          scheduleSafeWithdrawalProgress(progressUpdate, 'Calcolo prelievo sostenibile');
        },
        { signal: controller.signal, activityId, activityContext: swrActivityPlanRef.current?.context }
      );

      if (jobId !== swrSafeWithdrawalJobRef.current || run !== swrCompletedRunRef.current) return null;
      cancelScheduledSafeWithdrawalProgress();
      run.resultPatch.safeWithdrawalAnalysis = analysis;
      setSwrResults(prev => prev ? { ...prev, safeWithdrawalAnalysis: analysis } : prev);
      updateSavedSwrScenariosForRun(run, { safeWithdrawalAnalysis: analysis });
      setSafeWithdrawalProgressIfChanged({
        percent: 100,
        phase: isDetailed ? 'Analisi dettagliata completata' : 'Stima rapida completata',
      });
      return analysis;
    } catch (error) {
      if (isSWRAbortError(error)) return null;
      if (jobId === swrSafeWithdrawalJobRef.current) {
        console.error('Errore durante il calcolo delle card SWR:', error);
        cancelScheduledSafeWithdrawalProgress();
        setSafeWithdrawalProgressIfChanged({
          percent: 0,
          phase: 'Errore nel calcolo delle card',
        });
      }
      return null;
    } finally {
      if (jobId === swrSafeWithdrawalJobRef.current) {
        setSwrSafeWithdrawalIsLoading(false);
      }
    }
  }, [beginSWRWork, cancelScheduledSafeWithdrawalProgress, scheduleSafeWithdrawalProgress, setSafeWithdrawalProgressIfChanged, updateSavedSwrScenariosForRun]);

  const runSwrStressTestsForParams = useCallback(async (params, activityId) => {
    if (!params) return null;
    const run = swrCompletedRunRef.current;
    if (!run) return null;

    const jobId = ++swrStressTestsJobRef.current;
    const controller = beginSWRWork('stress');
    cancelScheduledStressTestsProgress();
    swrStressTestsLastProgressRef.current = null;
    setSwrStressTestsIsLoading(true);
    setStressTestsProgressIfChanged({ percent: 0, phase: 'Preparazione stress test' });

    try {
      const stressTests = await runSWRStressTests(params, (progressUpdate) => {
        if (jobId !== swrStressTestsJobRef.current) return;
        scheduleStressTestsProgress(progressUpdate, 'Calcolo stress test');
      }, { signal: controller.signal, activityId, activityContext: swrActivityPlanRef.current?.context });
      if (jobId !== swrStressTestsJobRef.current || run !== swrCompletedRunRef.current) return null;
      cancelScheduledStressTestsProgress();
      run.resultPatch.stressTests = stressTests;
      setSwrResults(prev => prev ? { ...prev, stressTests } : prev);
      updateSavedSwrScenariosForRun(run, { stressTests });
      setStressTestsProgressIfChanged({ percent: 100, phase: 'Stress test completati' });
      return stressTests;
    } catch (error) {
      if (isSWRAbortError(error)) return null;
      if (jobId === swrStressTestsJobRef.current) {
        console.error('Errore durante gli stress test SWR:', error);
        cancelScheduledStressTestsProgress();
        setStressTestsProgressIfChanged({ percent: 0, phase: 'Errore negli stress test' });
      }
      return null;
    } finally {
      if (jobId === swrStressTestsJobRef.current) {
        setSwrStressTestsIsLoading(false);
      }
    }
  }, [beginSWRWork, cancelScheduledStressTestsProgress, scheduleStressTestsProgress, setStressTestsProgressIfChanged, updateSavedSwrScenariosForRun]);

  const handleRunSwrSimulation = useCallback(async (params) => {
    cancelSWRActivityPlan(swrActivityPlanRef.current);
    const activityPlan = beginSWRActivityPlan(params, { mode: 'quick', numberOfSimulations: SWR_SAFE_WITHDRAWAL_ANALYSIS_MODES.quick.simulations });
    swrActivityPlanRef.current = activityPlan;
    const simulationJobId = ++swrSimulationJobRef.current;
    swrSafeWithdrawalJobRef.current += 1;
    swrStressTestsJobRef.current += 1;
    swrAbortRef.current.cards?.abort();
    swrAbortRef.current.stress?.abort();
    const controller = beginSWRWork('main');
    cancelScheduledSafeWithdrawalProgress();
    cancelScheduledStressTestsProgress();
    setSwrSafeWithdrawalIsLoading(false);
    swrSafeWithdrawalLastProgressRef.current = null;
    setSwrSafeWithdrawalProgress(null);
    setSwrSafeWithdrawalMode(SWR_SAFE_WITHDRAWAL_ANALYSIS_MODES.quick.id);
    setSwrStressTestsIsLoading(false);
    swrStressTestsLastProgressRef.current = null;
    setSwrStressTestsProgress(null);
    swrSimulationPendingRef.current = true;
    setSwrIsLoading(true);
    setSwrCurrentFormParams(params);
    setSwrProgress({ percent: 0, phase: 'Preparazione simulazione' });
    // Until success, the displayed result and its parameters stay paired.

    try {
      const simulationResults = await runSafeWithdrawalSimulation(params, (p) => {
        if (simulationJobId !== swrSimulationJobRef.current) return;
        setSwrProgress(typeof p === 'object' ? p : { percent: p, phase: 'Simulazione in corso' });
      }, { signal: controller.signal, activityId: activityPlan.main });
      if (simulationJobId !== swrSimulationJobRef.current) return;
      const effectiveParams = {
        ...params,
        effectiveRandomSeed: simulationResults?.summary?.seed ?? params.randomSeed,
        engineModelVersion: simulationResults?.engineModelVersion || params.engineModelVersion,
        valuationDate: simulationResults?.provenance?.units?.realMoneyBaseDate || params.valuationDate,
        provenance: simulationResults?.provenance || null,
        returnParameterUncertainty: simulationResults?.returnParameterUncertainty || null,
        macroMode: simulationResults?.macroMode || params.macroMode || 'fixed',
        macroSeed: simulationResults?.macroSeed ?? null,
        macroAssumptions: simulationResults?.macroAssumptions || null,
        macroSummary: simulationResults?.macroSummary || null,
      };
      const completedRun = { savedScenarioIds: new Set(), resultPatch: {}, archiveUpdates: Promise.resolve() };
      swrCompletedRunRef.current = completedRun;
      setSwrCompletedRun(completedRun);
      setSwrResults({
        ...simulationResults,
        safeWithdrawalAnalysis: null,
        stressTests: null,
      });
      setSwrHasRun(true);
      setSwrScenarioParams(effectiveParams);
      // Draft comparisons use the submitted input, not the derived numeric seed.
      setSwrLastRunParams(params);
      setSwrProgress({ percent: 100, phase: 'Completata' });
      runSafeWithdrawalCardsAnalysis(effectiveParams, SWR_SAFE_WITHDRAWAL_ANALYSIS_MODES.quick.id, activityPlan.cards)
        .finally(() => {
          if (simulationJobId === swrSimulationJobRef.current) {
            runSwrStressTestsForParams(effectiveParams, activityPlan.stress);
          }
        });
    } catch (error) {
      swrActivityMonitor.finish(activityPlan.cards, 'cancelled');
      swrActivityMonitor.finish(activityPlan.stress, 'cancelled');
      if (isSWRAbortError(error)) return;
      if (simulationJobId === swrSimulationJobRef.current) {
        console.error('Errore durante la simulazione SWR:', error);
        setSwrProgress({ percent: 0, status: 'error', phase: error.message || 'La simulazione non è riuscita. Rivedi i dati e riprova.' });
      }
    } finally {
      if (simulationJobId === swrSimulationJobRef.current) {
        swrSimulationPendingRef.current = false;
        setSwrIsLoading(false);
      }
    }
  }, [beginSWRWork, cancelScheduledSafeWithdrawalProgress, cancelScheduledStressTestsProgress, runSafeWithdrawalCardsAnalysis, runSwrStressTestsForParams]);

  const handleCancelSWR = useCallback(() => {
    cancelSWRActivityPlan(swrActivityPlanRef.current);
    swrSimulationJobRef.current++;
    swrSafeWithdrawalJobRef.current++;
    swrStressTestsJobRef.current++;
    Object.values(swrAbortRef.current).forEach(c => c?.abort());
    cancelScheduledSafeWithdrawalProgress();
    cancelScheduledStressTestsProgress();
    swrSimulationPendingRef.current = false;
    setSwrIsLoading(false);
    setSwrSafeWithdrawalIsLoading(false);
    setSwrStressTestsIsLoading(false);
    setSwrSafeWithdrawalProgress(null);
    setSwrStressTestsProgress(null);
    setSwrProgress({ percent: 0, status: 'cancelled', phase: 'Calcoli interrotti. Gli ultimi risultati completati restano disponibili; puoi avviare un nuovo calcolo.' });
  }, [cancelScheduledSafeWithdrawalProgress, cancelScheduledStressTestsProgress]);

  const handleRunDetailedSafeWithdrawalCards = useCallback(() => {
    if (!swrScenarioParams || swrSimulationPendingRef.current) return;
    runSafeWithdrawalCardsAnalysis(swrScenarioParams, SWR_SAFE_WITHDRAWAL_ANALYSIS_MODES.detailed.id);
  }, [runSafeWithdrawalCardsAnalysis, swrScenarioParams]);

  const handleRunSavedSwrScenario = useCallback((params) => {
    activateTab('swr');
    setSwrRestoredScenarioParams(params);
    setSwrRestoreKey(prev => prev + 1);
    // Restore into the form first: old assumptions must be reviewable before a new calculation.
  }, [activateTab]);

  const getHeaderInfo = () => {
    switch (activeTab) {
      case 'lombard':
        return {
          title: 'Credit Lombard',
          subtitle: 'Vista secondaria per strategie di credito Lombard',
          icon: CreditCard,
          gradient: 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-500/20'
        };
      case 'swr':
        return {
          title: 'Safe Withdrawal Rate',
          subtitle: 'Piano di prelievo sostenibile con analisi del capitale',
          icon: Wallet,
          gradient: 'bg-gradient-to-br from-blue-500 to-indigo-600 shadow-blue-500/20'
        };
      case 'swrCompare':
        return {
          title: 'Confronto SWR',
          subtitle: 'Confronto tra scenari Safe Withdrawal Rate salvati',
          icon: GitCompare,
          gradient: 'bg-gradient-to-br from-indigo-500 to-blue-600 shadow-indigo-500/20'
        };
      case 'compare':
        return {
          title: 'Confronto Strategie',
          subtitle: 'Vista secondaria di confronto tra Credit Lombard e SWR',
          icon: GitCompare,
          gradient: 'bg-gradient-to-br from-purple-500 to-violet-600 shadow-purple-500/20'
        };
      default:
        return {
          title: 'Longevity Income Architect',
          subtitle: 'Analisi finanziaria',
          icon: Calculator,
          gradient: 'bg-gradient-to-br from-slate-500 to-slate-600 shadow-slate-500/20'
        };
    }
  };

  const headerInfo = getHeaderInfo();
  const HeaderIcon = headerInfo.icon;
  const secondaryActive = activeTab === 'lombard' || activeTab === 'compare';
  const openSecondaryTab = (tab) => {
    activateTab(tab);
    setSecondaryViewsOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 overflow-x-clip">
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2 header-wrapper">
          <div className="flex min-h-11 items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <div className={`shrink-0 p-1 min-[400px]:p-1.5 rounded-lg ${headerInfo.gradient}`}>
                <HeaderIcon className="h-4 w-4 min-[400px]:h-5 min-[400px]:w-5 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xs min-[400px]:text-sm sm:text-base lg:text-lg leading-tight font-bold text-slate-800 header-title">{trDisplay("Longevity Income Architect")}</h1>
                <p className="text-xs text-slate-500 header-subtitle hidden lg:block">
                  {trDisplay(headerInfo.title)} - {trDisplay(headerInfo.subtitle)}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1 sm:gap-2 text-sm text-slate-500">
              <FeedbackButton />
              <LanguageSelector />
            </div>
          </div>

          <div className="mt-1 flex items-center gap-2 overflow-visible" data-testid="tab-navigation">
            <button
              type="button"
              aria-pressed={activeTab === 'swr'}
              onClick={() => activateTab('swr')}
              className={`flex h-11 items-center gap-2 px-3 sm:px-4 rounded-md font-semibold text-xs sm:text-sm transition-colors whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:opacity-80 ${
                activeTab === 'swr'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
              }`}
              data-testid="tab-swr"
            >
              <Wallet className="h-4 w-4" />
              <span className="hidden sm:inline">{trDisplay("Safe Withdrawal Rate")}</span>
              <span className="sm:hidden">{trDisplay("SWR")}</span>
            </button>

            <button
              type="button"
              aria-pressed={activeTab === 'swrCompare'}
              onClick={() => activateTab('swrCompare')}
              className={`flex h-11 items-center gap-2 px-3 sm:px-4 rounded-md font-semibold text-xs sm:text-sm transition-colors whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:opacity-80 ${
                activeTab === 'swrCompare'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
              }`}
              data-testid="tab-swr-compare"
            >
              <GitCompare className="h-4 w-4" />
              <span className="hidden sm:inline">{trDisplay("Confronto SWR")}</span>
              <span className="sm:hidden">{trDisplay("Confronto")}</span>
            </button>

            <div className="relative">
              <button
                type="button"
                onClick={() => setSecondaryViewsOpen(open => !open)}
                className={`flex h-11 min-w-11 items-center justify-center gap-2 px-2 sm:px-3 rounded-md font-medium text-xs sm:text-sm transition-colors whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:opacity-80 ${
                  secondaryActive
                    ? 'bg-slate-800 text-white shadow-sm'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}
                aria-expanded={secondaryViewsOpen}
                aria-label={trDisplay("Altre viste")}
                data-testid="secondary-views-toggle"
              >
                <MoreHorizontal className="h-4 w-4" />
                <span className="hidden sm:inline">{trDisplay("Altre viste")}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${secondaryViewsOpen ? 'rotate-180' : ''}`} />
              </button>

              {trDisplay(secondaryViewsOpen && (
                <div data-testid="secondary-views-menu" className="absolute right-0 sm:left-0 sm:right-auto mt-2 w-64 rounded-lg border border-slate-200 bg-white shadow-xl p-2 z-50">
                  <button
                    onClick={() => openSecondaryTab('lombard')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-colors ${
                      activeTab === 'lombard' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid="tab-lombard"
                  >
                    <CreditCard className="h-4 w-4" />
                    <span>{trDisplay("Credit Lombard")}</span>
                  </button>
                  <button
                    onClick={() => openSecondaryTab('compare')}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-colors ${
                      activeTab === 'compare' ? 'bg-purple-50 text-purple-700' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid="tab-compare"
                  >
                    <GitCompare className="h-4 w-4" />
                    <span>{trDisplay("Compara gli scenari")}</span>
                  </button>
                </div>
              ))}
            </div>
            <div className="ml-auto hidden xl:flex items-center gap-2 text-xs text-slate-500">
              <LineChart className="h-4 w-4" aria-hidden="true" />
              <span>{trDisplay("Analisi finanziaria client-side")}</span>
            </div>
          </div>
        </div>
        <SWRCalculationStatus onNavigate={kind => {
          activateTab('swr');
          requestAnimationFrame(() => {
            const target = document.getElementById(kind === 'cards' ? 'swr-safe-withdrawal' : kind === 'stress' ? 'swr-stress-tests' : kind === 'export' ? 'swr-export' : 'swr-main-status') || document.getElementById('swr-main-status');
            if (target) {
              const header = document.querySelector('header');
              window.scrollBy({ top: target.getBoundingClientRect().top - (header?.getBoundingClientRect().height || 0) - 12, behavior: 'auto' });
              if (target.tabIndex < 0 && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
              target.focus({ preventScroll: true });
            }
          });
        }} />
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 main-content-wrapper">
        <div style={{ display: activeTab === 'swr' ? 'block' : 'none' }}>
          <SafeWithdrawalPage
            results={swrResults}
            isLoading={swrIsLoading}
            progress={swrProgress}
            hasRun={swrHasRun}
            scenarioParams={swrScenarioParams}
            lastRunParams={swrLastRunParams}
            currentFormParams={swrCurrentFormParams}
            safeWithdrawalIsLoading={swrSafeWithdrawalIsLoading}
            safeWithdrawalProgress={swrSafeWithdrawalProgress}
            safeWithdrawalMode={swrSafeWithdrawalMode}
            safeWithdrawalDetailedSimulations={getSWRSafeWithdrawalDetailedSimulationCount(
              typeof window !== 'undefined' && window.innerWidth < 768
            )}
            onRunDetailedSafeWithdrawal={handleRunDetailedSafeWithdrawalCards}
            stressTestsIsLoading={swrStressTestsIsLoading}
            stressTestsProgress={swrStressTestsProgress}
            onSubmit={handleRunSwrSimulation}
            onCancel={handleCancelSWR}
            onParamsChange={handleSwrFormChange}
            restoredScenarioParams={swrRestoredScenarioParams}
            restoreKey={swrRestoreKey}
            onScenarioSaved={handleSwrScenarioSaved}
          />
        </div>

        {trDisplay(visitedTabs.has('compare') && (
          <div style={{ display: activeTab === 'compare' ? 'block' : 'none' }}>
            <Suspense fallback={<ViewLoadingFallback />}>
              <ComparisonTab
                lombardResults={lombardResults}
                lombardScenarioParams={lombardScenarioParams}
                swrResults={swrResults}
                swrScenarioParams={swrScenarioParams}
              />
            </Suspense>
          </div>
        ))}

        {trDisplay(visitedTabs.has('swrCompare') && (
          <div style={{ display: activeTab === 'swrCompare' ? 'block' : 'none' }}>
            <Suspense fallback={<ViewLoadingFallback />}>
              <SWRScenarioComparison onRunScenario={handleRunSavedSwrScenario} />
            </Suspense>
          </div>
        ))}

        {trDisplay(visitedTabs.has('lombard') && (
          <div style={{ display: activeTab === 'lombard' ? 'block' : 'none' }}>
            <Suspense fallback={<ViewLoadingFallback />}>
              <LegacyLombardPage
                results={lombardResults}
                isLoading={lombardIsLoading}
                progress={lombardProgress}
                hasRun={lombardHasRun}
                scenarioParams={lombardScenarioParams}
                hasPendingChanges={lombardHasPendingChanges}
                currentMaxLTV={currentMaxLTV}
                onSubmit={handleRunLombardSimulation}
                onParamsChange={handleLombardFormChange}
              />
            </Suspense>
          </div>
        ))}
      </main>

      <footer className="mt-8 sm:mt-16 border-t border-slate-200 bg-white/50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 footer-content">
            <div className="text-xs sm:text-sm text-slate-500 text-center sm:text-left footer-text">
              <p>{trDisplay("Longevity Income Architect • Tutti i calcoli avvengono localmente nel browser")}</p>
            </div>
            <div className="flex items-center gap-4 text-xs sm:text-sm text-slate-400">
              <span>{trDisplay("Monte Carlo Engine v1.0")}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
